import React from 'react';
import AgreementPageLayout from '../../components/AgreementPageLayout';

const CustomerAccountAgreementPage: React.FC = () => {
  return (
    <AgreementPageLayout
      title="AACore Customer User Agreement (Terms of Service)"
      effectiveDate="01/01/2026"
      lastUpdated="06/10/2026"
      toc={[
        { id: 'operator-contact', title: 'Operator & Contact' },
        { id: 'acceptance', title: '1) Acceptance of Terms' },
        { id: 'definitions', title: '2) Definitions' },
        { id: 'trial-period', title: '3) Enhancement, Testing, Trial Period, and Use at Your Own Risk' },
        { id: 'platform-only', title: '4) Platform Only; No Professional Services' },
        { id: 'independent-businesses', title: '5) Independent Businesses; No Agency or Employment' },
        { id: 'eligibility-account', title: '6) Eligibility; Account Registration; Accuracy; Security' },
        { id: 'communications', title: '7) Communications Consent; Email, SMS, and Notifications' },
        { id: 'bookings', title: '8) Booking Process; Confirmations; Changes' },
        { id: 'pricing-estimates', title: '9) Pricing, Estimates, Taxes, and Final Charges' },
        { id: 'payments', title: '10) Payments, Deposits, Tips, Stripe, Refunds, and Chargebacks' },
        { id: 'cancellations', title: '11) Cancellations, Late Arrivals, and No-Shows' },
        { id: 'service-issues', title: '12) Service Issues, Refunds, and Disputes with Businesses' },
        { id: 'health-safety', title: '13) Health, Safety, Suitability, and Service Risk' },
        { id: 'customer-responsibilities', title: '14) Customer Responsibilities and Prohibited Conduct' },
        { id: 'user-content', title: '15) User Content, Reviews, Ratings, and Messaging' },
        { id: 'privacy-data', title: '16) Privacy and Customer Data' },
        { id: 'third-party-services', title: '17) Third-Party Services, Links, and Integrations' },
        { id: 'availability', title: '18) Availability, Accuracy, and No Uptime Commitment' },
        { id: 'intellectual-property', title: '19) Intellectual Property' },
        { id: 'suspension-termination', title: '20) Suspension, Termination, and Refusal of Access' },
        { id: 'disclaimers', title: '21) Disclaimer of Warranties' },
        { id: 'release', title: '22) Release' },
        { id: 'limitation-liability', title: '23) Limitation of Liability' },
        { id: 'indemnification', title: '24) Indemnification' },
        { id: 'force-majeure', title: '25) Force Majeure' },
        { id: 'dispute-resolution', title: '26) Dispute Resolution; Arbitration; Class Action Waiver' },
        { id: 'governing-law', title: '27) Governing Law; Venue' },
        { id: 'changes', title: '28) Changes to Terms' },
        { id: 'miscellaneous', title: '29) Assignment; Severability; No Waiver; Entire Agreement' },
        { id: 'contact', title: '30) Contact' },
        { id: 'agreement-acknowledgment', title: 'Agreement Acknowledgment' },
      ]}
    >
      <p>
        These Terms govern customer use of AACore, including AACore Booking and related AACore features. AACore is a
        technology platform used by independent Businesses. AAI does not provide the Business Services, does not control
        Business operations, is not a payment processor, and does not hold full payment card or banking information.
      </p>

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="font-semibold mb-1">Important Customer, Trial, and Payments Notice</div>
        AACore Booking is provided during an enhancement, testing, and trial period through December 31, 2026, unless
        A&amp;A Inomatic, LLC announces a different date or service model. The Platform is provided &ldquo;as is,&rdquo;
        &ldquo;as available,&rdquo; and &ldquo;at your own risk.&rdquo; Businesses using AACore Booking are independent
        providers responsible for their own services, pricing, policies, refunds, taxes, licenses, staff, safety, and
        customer care. Payment services are provided by third-party Payment Processors, such as Stripe, not by AAI.
      </div>

      <div id="operator-contact" className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <div className="font-semibold text-gray-900 mb-1">Operator &amp; Contact</div>
        <strong>Platform:</strong> AACore, including AACore Booking and related modules (the &ldquo;Platform&rdquo;)
        <br />
        <strong>Operator:</strong> A&amp;A Inomatic, LLC (&ldquo;AAI,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; &ldquo;our&rdquo;)
        <br />
        <strong>Support:</strong> <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>
        <br />
        <strong>Address:</strong> Clifton, VA 20124
        <br />
        <strong>Privacy:</strong> <a href="/privacy">AACore Privacy Policy</a>
        <p className="mt-3 mb-0">
          This AACore Customer User Agreement and Terms of Service (&ldquo;Terms&rdquo; or &ldquo;Agreement&rdquo;)
          governs use of the Platform by any individual or organization that visits, books, requests services,
          communicates, pays, purchases, submits content, or otherwise interacts with a Business through the Platform
          (&ldquo;Customer,&rdquo; &ldquo;you,&rdquo; &ldquo;your&rdquo;).
        </p>
      </div>

      <h3 id="acceptance">1) Acceptance of Terms</h3>
      <p>
        By accessing the Platform, creating an account, using a guest checkout or guest booking flow, clicking
        &ldquo;I Agree,&rdquo; booking an appointment, requesting a quote, submitting a form, communicating with a
        Business, making a payment, purchasing a gift card, or otherwise using AACore Booking, you confirm that you have
        read, understood, and agree to these Terms and the <a href="/privacy">AACore Privacy Policy</a>.
      </p>
      <p>
        You agree that these Terms are a legally binding agreement and that your electronic acceptance has the same legal
        effect as a physical signature, to the maximum extent permitted by law.
      </p>
      <p>
        If you do not agree, do not use the Platform, do not submit information through the Platform, and do not complete
        bookings or payments through the Platform.
      </p>
      <p>
        If you use the Platform on behalf of another person, minor, dependent, organization, or household member, you
        represent that you have authority to do so and that all information you provide is accurate and authorized.
      </p>

      <h3 id="definitions">2) Definitions</h3>
      <ul>
        <li>
          <strong>&ldquo;AAI,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;</strong>{' '}
          means A&amp;A Inomatic, LLC.
        </li>
        <li>
          <strong>&ldquo;Platform&rdquo;</strong> means AACore, including AACore Booking and related AACore applications,
          websites, pages, booking flows, communications, administrative features, and mobile experiences.
        </li>
        <li>
          <strong>&ldquo;Business&rdquo;</strong> means an independent third-party business, provider, professional,
          office, store, organization, merchant, staff member, contractor, or service provider that uses the Platform to
          offer, schedule, sell, manage, or communicate about its services or products.
        </li>
        <li>
          <strong>&ldquo;Business Services&rdquo; or &ldquo;Services&rdquo;</strong> means services, products,
          appointments, consultations, events, quotes, gift cards, deposits, policies, or other offerings provided, sold,
          performed, or managed by a Business.
        </li>
        <li>
          <strong>&ldquo;Customer Data&rdquo;</strong> means information you submit to or generate through the Platform,
          including name, contact information, appointment details, service selections, notes, form responses, messages,
          preferences, payment-related metadata, and related records.
        </li>
        <li>
          <strong>&ldquo;User Content&rdquo;</strong> means content you submit through the Platform, including ratings,
          reviews, text, images, videos, files, form responses, messages, special requests, and other information.
        </li>
        <li>
          <strong>&ldquo;Payment Processor&rdquo;</strong> means Stripe or another third-party payment provider that
          processes online payments, deposits, tips, refunds, payouts, disputes, chargebacks, identity verification, or
          related payment services.
        </li>
        <li>
          <strong>&ldquo;Payment Data&rdquo;</strong> means payment card numbers, CVV codes, bank account details,
          transaction records, payment tokens, processor identifiers, payment status, payment intent identifiers, charge
          identifiers, refund identifiers, limited card descriptors, and related payment-processing information.
        </li>
        <li>
          <strong>&ldquo;Service Providers&rdquo;</strong> means third parties used by AAI to operate, host, support,
          secure, communicate, analyze, or improve the Platform, including hosting, database, email, SMS, analytics,
          logging, security, and payment-related vendors.
        </li>
        <li>
          <strong>&ldquo;Company Parties&rdquo;</strong> means AAI and its owners, officers, directors, managers,
          employees, contractors, agents, affiliates, licensors, vendors, and Service Providers.
        </li>
        <li>
          <strong>&ldquo;Applicable Law&rdquo;</strong> means all laws, rules, regulations, consumer protection
          obligations, privacy obligations, tax obligations, payment rules, card network rules, licensing requirements,
          and industry obligations applicable to you, the Business, the Business Services, or your use of the Platform.
        </li>
      </ul>

      <h3 id="trial-period">3) Enhancement, Testing, Trial Period, and Use at Your Own Risk</h3>
      <p>
        AACore Booking is currently being provided to participating Businesses during a free enhancement, testing, and
        trial period through December 31, 2026, unless AAI provides a different written date, transitions to a paid plan,
        ends free access, or changes the service model.
      </p>
      <p>
        The Platform may contain defects, unfinished features, design changes, downtime, configuration changes, workflow
        changes, incorrect results, removed features, or other limitations. You understand and agree that your use of the
        Platform is at your own risk.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>Customer verification reminder.</strong> You should confirm important appointment details, prices, taxes,
          deposits, cancellation rules, refund rules, eligibility requirements, service instructions, safety information,
          and payment details directly with the Business when the matter is important, time-sensitive, or affects your
          decision to book or pay.
        </p>
      </div>
      <p>
        AAI may modify, suspend, limit, or discontinue any free, trial, beta, early access, or enhancement-period feature
        at any time without liability, refund, credit, service-level remedy, or obligation to continue offering the
        feature.
      </p>

      <h3 id="platform-only">4) Platform Only; No Professional Services</h3>
      <p>
        AAI operates AACore as a technology platform. AAI does not provide, perform, supervise, inspect, endorse, guarantee,
        warrant, or control any Business Services. AAI is not a medical provider, dental provider, salon, clinic, spa,
        wellness provider, home service provider, transportation provider, retail seller, employer, staffing company,
        payment processor, bank, or insurer.
      </p>
      <p>
        The Platform may display service descriptions, prices, durations, business profiles, photos, policies,
        availability, reviews, directions, reminders, and other information provided by Businesses or users. This
        information is for convenience only and is not professional, medical, dental, legal, financial, tax, health,
        safety, or other professional advice from AAI.
      </p>
      <p>
        Your decision to book, pay for, receive, decline, cancel, reschedule, or continue with any Business Service is your
        responsibility. You should ask the Business or a qualified professional for advice when needed.
      </p>

      <h3 id="independent-businesses">5) Independent Businesses; No Agency or Employment</h3>
      <p>
        Businesses are independent third parties and are not employees, agents, joint venturers, partners, franchisees,
        representatives, contractors, or affiliates of AAI unless AAI expressly states otherwise in a written agreement
        signed by AAI.
      </p>
      <p>
        AAI does not control how Businesses perform Services, does not supervise Business staff, does not verify every
        license or credential, does not inspect Business locations, and is not responsible for Business acts or omissions.
      </p>
      <p>Businesses are solely responsible for:</p>
      <ul>
        <li>service quality, outcomes, safety, sanitation, staffing, supervision, and customer care;</li>
        <li>licensing, certifications, permits, insurance, inspections, and legal compliance;</li>
        <li>service descriptions, pricing, taxes, fees, availability, staff assignments, and scheduling decisions;</li>
        <li>cancellation, refund, reschedule, no-show, late-arrival, deposit, gift card, and customer policies; and</li>
        <li>resolving service-related complaints, injuries, refunds, disputes, and chargebacks.</li>
      </ul>

      <h3 id="eligibility-account">6) Eligibility; Account Registration; Accuracy; Security</h3>
      <p>
        You must be at least eighteen (18) years old, or the age of majority in your jurisdiction, to create an account or
        make a booking unless a parent, legal guardian, or authorized adult is acting for you. If you book for a minor or
        dependent, you represent that you have legal authority to do so and to share any necessary information.
      </p>
      <p>
        You may be able to use some Platform features as a guest and others through a customer profile or login. You agree
        to provide accurate, current, and authorized information, including name, phone number, email address, appointment
        details, form responses, and payment information, and to keep your information updated.
      </p>
      <p>
        You are responsible for all activity under your account and for protecting login credentials, verification codes,
        devices, browsers, and communications. You must promptly notify AAI or the Business if you suspect unauthorized
        access.
      </p>
      <p>
        You must not impersonate another person, use another person&apos;s information without permission, create false
        bookings, provide false contact information, or use the Platform for deceptive, abusive, unlawful, or disruptive
        purposes.
      </p>

      <h3 id="communications">7) Communications Consent; Email, SMS, and Notifications</h3>
      <p>
        By creating an account, booking, requesting a service, submitting contact information, or otherwise using the
        Platform, you consent to receive transactional communications by email, SMS, phone, browser notification, or other
        channels. These communications may include verification codes, booking confirmations, reminders, schedule
        changes, receipts, payment notices, security alerts, support messages, quote updates, and account notices.
      </p>
      <p>
        Message and data rates may apply. Delivery is not guaranteed and may be delayed, blocked, filtered, duplicated, or
        misdirected due to carriers, spam filters, device settings, inbox rules, outages, incorrect contact information,
        third-party provider issues, or Business configuration.
      </p>
      <p>
        You may opt out of promotional messages where available. Transactional messages may still be sent as needed to
        operate your account, bookings, payments, security, and support.
      </p>
      <p>AAI is not liable for delayed, failed, misdirected, filtered, duplicated, or undelivered messages.</p>

      <h3 id="bookings">8) Booking Process; Confirmations; Changes</h3>
      <p>
        A booking may be instantly confirmed or may require Business approval. Businesses may accept, reject, modify,
        reschedule, shorten, extend, or cancel bookings based on availability, staffing, emergencies, customer eligibility,
        payment status, policy reasons, or other Business needs.
      </p>
      <p>
        Bookings, quote requests, service requests, waitlists, forms, and appointment communications are between you and
        the Business. The Platform may help transmit or display information, but AAI does not guarantee that any
        appointment, request, quote, service, price, staff assignment, location, duration, availability, or policy is
        accurate, available, suitable, or will be honored by the Business.
      </p>
      <p>
        AAI is not responsible for missed appointments, Business cancellations, scheduling conflicts, late arrivals,
        incorrect calendar information, incorrect location information, or Business failure to honor a booking.
      </p>

      <h3 id="pricing-estimates">9) Pricing, Estimates, Taxes, and Final Charges</h3>
      <p>
        Prices, deposits, durations, service descriptions, discounts, taxes, and fees shown through the Platform may be
        estimates or may depend on information provided by the Business. Final pricing may change based on actual Services
        rendered, add-ons, upgrades, modifications, time required, complexity, materials, product usage, condition,
        requirements, Business policies, local taxes, and fees.
      </p>
      <p>
        The final total is determined by the Business and reflects the actual Business Services provided or charged under
        the Business&apos;s policies. AAI does not set, verify, guarantee, or control Business prices, taxes, fees, tips,
        deposits, discounts, gift card rules, or final charges.
      </p>
      <p>
        You are responsible for reviewing the Business&apos;s pricing and policies before booking or paying and for asking
        the Business directly about any unclear or time-sensitive pricing issue.
      </p>

      <h3 id="payments">10) Payments, Deposits, Tips, Stripe, Refunds, and Chargebacks</h3>
      <p>
        If you make an online payment, deposit, tip, invoice payment, gift card purchase, or related transaction through the
        Platform, payment processing services are provided by a Payment Processor such as Stripe, not by AAI.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p className="mb-0">
          <strong>No Stripe affiliation and no banking custody.</strong> AAI is not owned by, operated by, sponsored by,
          endorsed by, affiliated with, or acting as an agent of Stripe. AAI is not a bank, payment processor, money
          transmitter, escrow agent, merchant of record, financial institution, credit provider, payment facilitator, payment
          intermediary, or fiduciary for Customer funds. AAI does not hold, control, receive custody of, settle, or
          guarantee Customer funds, Business payouts, deposits, refunds, reversals, or chargeback outcomes.
        </p>
      </div>

      <h4>10.1 Payment authorization</h4>
      <p>
        When you submit or approve a payment through a Payment Processor-controlled flow, you authorize the applicable
        Business and Payment Processor to charge your selected payment method for the amounts you approve or that are
        permitted under the Business policies presented to you, including deposits, service charges, applicable taxes and
        fees, cancellation or no-show fees, and tips where applicable. AAI may transmit payment instructions, status
        updates, or limited payment metadata as part of operating the Platform, but AAI does not process, hold, or settle
        your payment card or bank funds.
      </p>

      <h4>10.2 Payment Processor terms</h4>
      <p>
        Your payment may be subject to the Payment Processor&apos;s terms, privacy notices, fraud controls, identity
        verification, card network rules, dispute rules, refund processes, and data practices. AAI is not responsible for
        Payment Processor decisions, outages, errors, holds, declines, duplicate charges, fees, disputes, verification
        requests, account restrictions, or data practices.
      </p>

      <h4>10.3 No storage of full payment card or banking information</h4>
      <p>
        AAI does not store full payment card numbers, CVV codes, full bank account numbers, full routing numbers, or full
        payout banking credentials. Full payment card and banking information should be entered only into
        Payment Processor-controlled flows or hosted payment interfaces. The Platform may store or display limited
        payment-related metadata received from a Payment Processor, such as payment status, transaction identifiers,
        customer identifiers, connected account identifiers, invoice identifiers, payment intent identifiers, refund
        identifiers, limited card descriptors, timestamps, amounts, and audit records.
      </p>

      <h4>10.4 Business controls prices and refunds</h4>
      <p>
        The Business is responsible for prices, taxes, tips, deposits, gift card rules, cancellation fees, no-show fees,
        refund policies, refund decisions, chargeback responses, and transaction records. AAI does not set Business
        prices, approve refunds, guarantee refunds, adjudicate service disputes, or control chargeback outcomes.
      </p>

      <h4>10.5 Failed payments and incorrect charges</h4>
      <p>
        Payments may fail, duplicate, be delayed, be declined, be reversed, or be placed under review due to processor
        systems, bank/card issuer decisions, network issues, fraud controls, user error, Business settings, or other
        causes. You should contact the Business and, where appropriate, your bank/card issuer or the Payment Processor
        for payment issues.
      </p>
      <p>
        Chargebacks, payment disputes, suspected fraud, or payment abuse may result in account limitation, suspension,
        cancellation of bookings, or refusal of future access.
      </p>

      <h3 id="cancellations">11) Cancellations, Late Arrivals, and No-Shows</h3>
      <p>
        Each Business sets its own cancellation, reschedule, late-arrival, deposit, no-show, refund, and service
        eligibility policies. By booking, you agree to the Business policies shown at booking time or otherwise
        communicated by the Business.
      </p>
      <p>
        AAI does not create or control Business policies and is not responsible for cancellation fees, no-show fees,
        late-arrival decisions, reschedule denials, deposit forfeitures, or Business decisions to refuse or end service,
        except where required by law and directly applicable to AAI.
      </p>
      <p>
        AAI may enforce platform-wide protections, including account restrictions for repeated no-shows, abusive behavior,
        suspected fraud, excessive disputes, or chargebacks.
      </p>

      <h3 id="service-issues">12) Service Issues, Refunds, and Disputes with Businesses</h3>
      <p>
        Service quality disputes must be addressed directly with the Business. If payment was processed through the
        Platform, AAI may, in its discretion, assist with documentation or communication, but Businesses are responsible
        for Business Service outcomes and refund decisions except where required by law or payment network rules.
      </p>
      <p>
        AAI is not a party to your Business Service transaction, does not guarantee any Business Service, and is not
        obligated to mediate, investigate, resolve, refund, reimburse, or compensate you for disputes with a Business.
      </p>
      <p>
        Complaints involving service quality, staff conduct, injuries, dissatisfaction, malpractice, negligence, product
        defects, sanitation, safety, taxes, prices, refunds, cancellations, or no-show fees should be directed to the
        Business and, where appropriate, to the relevant regulator, payment provider, bank, or card issuer.
      </p>

      <h3 id="health-safety">13) Health, Safety, Suitability, and Service Risk</h3>
      <p>
        You are responsible for providing accurate information relevant to your Business Service, including allergies,
        sensitivities, medical conditions, access needs, age or eligibility information, safety concerns, and other
        information requested by the Business. Some Business Services may not be suitable for all individuals.
      </p>
      <p>
        Business Services may involve personal care, health-related services, dental-related services, physical activity,
        chemicals, tools, equipment, transportation, locations, facilities, or other risks depending on the Business. AAI
        does not evaluate whether any Business Service is safe, suitable, licensed, sanitary, insured, or appropriate for
        you.
      </p>
      <p>
        For any health-related, dental-related, medical, legal, financial, or safety concern, consult a qualified
        professional. In emergencies, call local emergency services.
      </p>
      <p>
        Any injury, allergic reaction, infection, illness, loss, property damage, service defect, dissatisfaction, or
        other issue arising from Business Services is between you and the Business, not AAI.
      </p>

      <h3 id="customer-responsibilities">14) Customer Responsibilities and Prohibited Conduct</h3>
      <p>You are responsible for using the Platform lawfully, respectfully, and in good faith. You must not use the Platform to:</p>
      <ul>
        <li>
          violate any law, regulation, third-party right, payment rule, privacy rule, consumer protection rule, or Business
          policy;
        </li>
        <li>
          make false, fraudulent, abusive, duplicate, automated, or bad-faith bookings, requests, payments, disputes,
          chargebacks, or communications;
        </li>
        <li>
          harass, threaten, impersonate, defraud, discriminate against, or abuse any Business, Customer, staff member, AAI
          representative, Service Provider, or Payment Processor;
        </li>
        <li>submit unlawful, misleading, infringing, harmful, obscene, defamatory, private, or malicious content;</li>
        <li>submit false reviews, manipulate ratings, or misrepresent a service experience;</li>
        <li>scrape, reverse engineer, resell, sublicense, disrupt, overload, probe, bypass, or interfere with the Platform;</li>
        <li>
          attempt unauthorized access to accounts, systems, source code, databases, credentials, or non-public Platform
          information;
        </li>
        <li>
          circumvent payment obligations, processor rules, security controls, access controls, rate limits, or enforcement
          actions; or
        </li>
        <li>
          use the Platform in a way that creates legal, payment, security, operational, or reputational risk for AAI,
          Businesses, Customers, Service Providers, or Payment Processors.
        </li>
      </ul>

      <h3 id="user-content">15) User Content, Reviews, Ratings, and Messaging</h3>
      <p>
        If you submit User Content, you represent and warrant that you own it or have permission to submit it, that it is
        accurate to the best of your knowledge, and that it does not violate law, privacy rights, publicity rights,
        intellectual property rights, Business policies, or third-party rights.
      </p>
      <p>
        You grant Company Parties a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host,
        store, reproduce, distribute, transmit, display, perform, create derivative works of, and otherwise use User
        Content in connection with operating, securing, supporting, improving, and marketing the Platform, including showing
        reviews or ratings to other users, unless prohibited by law.
      </p>
      <p>
        AAI may remove, restrict, moderate, preserve, or disclose User Content at any time where appropriate to operate
        the Platform, enforce this Agreement, address security or payment concerns, comply with law, respond to complaints,
        or protect AAI, Businesses, Customers, Service Providers, Payment Processors, or the public.
      </p>

      <h3 id="privacy-data">16) Privacy and Customer Data</h3>
      <p>
        Your use of the Platform is governed by the <a href="/privacy">AACore Privacy Policy</a>. By using the Platform,
        you acknowledge that Customer Data may be processed by AAI, Businesses, Payment Processors, and Service Providers
        as needed to operate the Platform, process bookings, communicate, support payments, prevent fraud, secure the
        Platform, provide support, improve features, and comply with law.
      </p>
      <p>
        Businesses may have their own privacy practices and policies. AAI is not responsible for a Business&apos;s
        independent collection, use, retention, disclosure, security, deletion, or handling of Customer Data outside
        AAI&apos;s role as the Platform operator.
      </p>
      <p>
        You should not submit highly sensitive information unless it is necessary for the Business Service and you are
        comfortable providing it to the Business. Do not enter full payment card numbers, CVV codes, bank account numbers,
        Social Security numbers, government identification numbers, medical records, or other highly sensitive information
        into free-text fields, messages, notes, reviews, or uploaded files.
      </p>

      <h3 id="third-party-services">17) Third-Party Services, Links, and Integrations</h3>
      <p>
        The Platform may depend on or connect to third-party services, including hosting providers, email providers, SMS
        providers, analytics tools, maps, browsers, device operating systems, app stores, Payment Processors, banks, card
        issuers, and other integrations.
      </p>
      <p>
        Third-party services are controlled by their own providers and may be subject to separate terms, fees, restrictions,
        outages, changes, and privacy practices. AAI is not responsible for third-party services, third-party content,
        third-party outages, third-party pricing, third-party decisions, third-party data practices, third-party payment
        actions, or your compliance with third-party terms.
      </p>

      <h3 id="availability">18) Availability, Accuracy, and No Uptime Commitment</h3>
      <p>
        The Platform may be unavailable, delayed, slow, incomplete, inaccurate, interrupted, or changed due to maintenance,
        upgrades, bugs, capacity limits, data migration, security work, hosting issues, internet outages, communication
        provider issues, Payment Processor outages, Business configuration, user error, or other events outside AAI&apos;s
        control.
      </p>
      <p>
        AAI does not guarantee uninterrupted service, error-free service, data availability, message delivery, payment
        availability, booking availability, appointment accuracy, Business availability, price accuracy, policy accuracy, or
        any specific uptime percentage.
      </p>
      <p>
        AAI is not responsible for missed appointments, incorrect appointment details, incorrect prices, incorrect policies,
        failed messages, failed payments, user error, Business configuration mistakes, or third-party failures.
      </p>

      <h3 id="intellectual-property">19) Intellectual Property</h3>
      <p>
        The Platform, including software, design, logos, branding, user interfaces, workflows, content, documentation,
        database structure, templates, reports, and functionality, is owned by AAI or its licensors and is protected by
        law.
      </p>
      <p>
        You receive a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to use the Platform
        for personal, non-commercial customer purposes, subject to these Terms.
      </p>
      <p>
        You may not copy, resell, lease, sublicense, distribute, modify, create derivative works of, reverse engineer,
        decompile, publicly benchmark, or commercially exploit the Platform except as expressly permitted by law or written
        permission from AAI.
      </p>

      <h3 id="suspension-termination">20) Suspension, Termination, and Refusal of Access</h3>
      <p>AAI may suspend, limit, disable, refuse, or terminate access to the Platform or specific features immediately if:</p>
      <ul>
        <li>you violate this Agreement or Applicable Law;</li>
        <li>your use creates legal, payment, security, reputational, operational, or compliance risk;</li>
        <li>
          fraud, abuse, excessive disputes, repeated no-shows, illegal activity, harassment, or unauthorized access is
          suspected;
        </li>
        <li>
          a Business, Service Provider, Payment Processor, court, regulator, or law enforcement request requires or
          encourages action;
        </li>
        <li>
          your information, payment activity, booking activity, or communications appear inaccurate, misleading, abusive, or
          unauthorized; or
        </li>
        <li>your use harms AAI, Businesses, Customers, Service Providers, Payment Processors, or Platform stability.</li>
      </ul>
      <p>
        You may stop using the Platform at any time. Termination or suspension of Platform access does not automatically
        cancel obligations you may have to a Business, Payment Processor, bank, card issuer, or other third party.
      </p>

      <h3 id="disclaimers">21) Disclaimer of Warranties</h3>
      <p>
        To the maximum extent permitted by law, the Platform, including AACore Booking, is provided &ldquo;as is,&rdquo;
        &ldquo;as available,&rdquo; &ldquo;with all faults,&rdquo; and &ldquo;at your own risk.&rdquo;
      </p>
      <p>
        Company Parties disclaim all warranties, whether express, implied, statutory, or otherwise, including warranties of
        merchantability, fitness for a particular purpose, title, non-infringement, accuracy, reliability, availability,
        security, uninterrupted use, error-free operation, compatibility, and any warranty arising from course of dealing
        or usage of trade.
      </p>
      <p>
        AAI does not warrant that the Platform, any Business, or any Business Service will meet your expectations, be
        available, be honored, be safe, be suitable, be properly licensed, comply with laws, prevent missed appointments,
        prevent payment disputes, produce accurate prices or taxes, or satisfy any particular outcome.
      </p>

      <h3 id="release">22) Release</h3>
      <p>
        To the maximum extent permitted by law, you release Company Parties from claims, demands, actions, losses, damages,
        liabilities, expenses, and disputes arising out of or related to:
      </p>
      <ul>
        <li>
          Business Services, Business operations, Business staff, Business policies, Business prices, taxes, refunds,
          deposits, gift cards, cancellations, no-show rules, and Business compliance with law;
        </li>
        <li>
          Business cancellations, reschedules, service outcomes, injuries, allergic reactions, infections, illness,
          negligence, malpractice, product defects, sanitation issues, staff misconduct, property damage, or
          dissatisfaction;
        </li>
        <li>
          disputes between you and a Business, Business owner, staff member, contractor, Payment Processor, bank, card
          issuer, or other third party;
        </li>
        <li>
          Payment Processor actions, Stripe-related actions, processor downtime, payment failures, duplicate charges,
          withheld funds, payout delays, reversals, reserves, identity verification, and dispute outcomes;
        </li>
        <li>
          third-party outages involving hosting, internet, email, SMS, carriers, browsers, devices, operating systems, app
          stores, maps, Payment Processors, banks, card issuers, or other providers; and
        </li>
        <li>
          your data entry, account security, devices, credentials, communications, payment decisions, and failure to confirm
          important information directly with the Business.
        </li>
      </ul>
      <p>This release does not apply where prohibited by law.</p>

      <h3 id="limitation-liability">23) Limitation of Liability</h3>
      <p>
        To the maximum extent permitted by law, Company Parties will not be liable for indirect, incidental, special,
        consequential, exemplary, punitive, enhanced, or similar damages, including lost profits, lost opportunities, lost
        goodwill, reputational harm, loss of data, service interruption, missed appointments, failed messages, payment
        failure, chargebacks, refunds, processor holds, travel costs, replacement services, emotional distress, personal
        injury, property damage, or Business interruption, even if advised of the possibility of such damages.
      </p>
      <p>
        To the maximum extent permitted by law, Company Parties are not liable for any Business Service, Business action
        or omission, Business policy, pricing error, tax error, refund denial, cancellation, no-show fee, service defect,
        injury, loss, customer dissatisfaction, third-party failure, Payment Processor action, data loss, security incident
        not caused solely by AAI&apos;s willful misconduct, user error, configuration mistake, or communications failure.
      </p>
      <p>
        AAI&apos;s total aggregate liability for all claims related to the Platform or this Agreement will not exceed the
        greater of: (a) the amount you paid directly to AAI, if any, for Platform access during the three (3) months before
        the event giving rise to the claim; or (b) one hundred dollars ($100), unless a higher amount is required by law and
        cannot be limited by contract. Amounts paid to a Business or Payment Processor for Business Services are not amounts
        paid directly to AAI for Platform access.
      </p>
      <p>
        Some jurisdictions do not allow certain limitations, so these limits apply to the fullest extent permitted. For
        free, trial, beta, early access, or enhancement-period use, you acknowledge that AAI is providing the Platform
        without charging you a customer subscription fee and that this limitation of liability is a fundamental condition of
        that access.
      </p>

      <h3 id="indemnification">24) Indemnification</h3>
      <p>
        You agree to defend, indemnify, and hold harmless Company Parties from and against all claims, demands, actions,
        investigations, damages, liabilities, fines, penalties, losses, settlements, costs, and expenses, including
        reasonable attorneys&apos; fees, arising out of or related to:
      </p>
      <ul>
        <li>
          your use or misuse of the Platform, violation of this Agreement, violation of Applicable Law, or violation of
          third-party rights;
        </li>
        <li>User Content, information, forms, messages, files, reviews, ratings, or data you submit through the Platform;</li>
        <li>
          false, fraudulent, abusive, unauthorized, or bad-faith bookings, requests, payments, disputes, chargebacks,
          communications, reviews, or claims;
        </li>
        <li>
          your account compromise, weak security, unauthorized use of your credentials, user error, device compromise, or
          failure to update account information;
        </li>
        <li>
          your disputes with Businesses, staff, other Customers, Payment Processors, banks, card issuers, or other third
          parties; and
        </li>
        <li>
          your breach of representations, warranties, consents, authorizations, or obligations under these Terms, except to
          the extent caused solely by AAI&apos;s willful misconduct.
        </li>
      </ul>

      <h3 id="force-majeure">25) Force Majeure</h3>
      <p>
        AAI is not responsible for delays, failures, data loss, unavailability, or inability to perform caused by events
        beyond its reasonable control, including natural disasters, fire, flood, weather, power failures, internet outages,
        carrier outages, hosting failures, Payment Processor failures, cyberattacks, malware, pandemics, war, terrorism,
        civil unrest, labor disputes, supply chain issues, government actions, legal restrictions, emergencies, or vendor
        failures.
      </p>

      <h3 id="dispute-resolution">26) Dispute Resolution; Arbitration; Class Action Waiver</h3>
      <p>
        <strong>Please read carefully.</strong> This section affects your legal rights.
      </p>
      <p>
        <strong>Informal first.</strong> Before filing a claim against AAI, you agree to contact{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a> and allow thirty (30) days for AAI to attempt to
        resolve the matter informally.
      </p>
      <p>
        <strong>Business disputes.</strong> Disputes about Business Services, prices, refunds, cancellations, no-show fees,
        staff conduct, service quality, injuries, taxes, or Business policies should be directed to the Business first
        because AAI is not the provider of the Business Services.
      </p>
      <p>
        <strong>Arbitration.</strong> Except for small claims matters or claims for injunctive relief, you and AAI agree
        that any dispute arising out of or related to these Terms or the Platform will be resolved by binding individual
        arbitration in Virginia or remotely by video where appropriate, administered by a recognized arbitration provider
        under applicable consumer arbitration rules.
      </p>
      <p>
        <strong>Class action waiver.</strong> You and AAI agree to bring claims only on an individual basis. No class
        actions, class arbitrations, collective actions, consolidated actions, private attorney general actions, or
        representative actions are permitted, to the maximum extent permitted by law.
      </p>
      <p>
        <strong>Injunctive relief.</strong> Either party may seek injunctive or equitable relief in court for intellectual
        property misuse, confidentiality violations, security threats, unauthorized access, payment fraud, or misuse of the
        Platform.
      </p>
      <p>If any part of this section is found unenforceable, the remainder will be enforced to the greatest extent permitted.</p>

      <h3 id="governing-law">27) Governing Law; Venue</h3>
      <p>
        Virginia law governs these Terms and any dispute related to the Platform, excluding conflict-of-law rules. If a
        dispute is not subject to arbitration, it will be brought exclusively in the state or federal courts located in
        Virginia, and you consent to personal jurisdiction and venue in those courts.
      </p>

      <h3 id="changes">28) Changes to Terms</h3>
      <p>
        AAI may update these Terms from time to time. The &ldquo;Last updated&rdquo; date will reflect changes. Continued use
        of the Platform after updates become effective constitutes acceptance of the updated Terms. If changes are material,
        AAI will provide reasonable notice through the Platform, email, account notice, or other reasonable method.
      </p>
      <p>If you do not agree to updated Terms, you must stop using the Platform.</p>

      <h3 id="miscellaneous">29) Assignment; Severability; No Waiver; Entire Agreement</h3>
      <p>
        <strong>Assignment.</strong> You may not assign your rights or obligations under these Terms without AAI&apos;s prior
        written consent. AAI may assign these Terms in connection with a merger, acquisition, reorganization, financing,
        asset sale, corporate transaction, change of control, or transfer of the Platform.
      </p>
      <p>
        <strong>Severability.</strong> If any provision is unenforceable, the remaining provisions will remain in effect,
        and the unenforceable provision will be modified to the minimum extent necessary to make it enforceable where
        possible.
      </p>
      <p>
        <strong>No waiver.</strong> AAI&apos;s failure to enforce a provision is not a waiver of its right to enforce that
        provision later.
      </p>
      <p>
        <strong>Consumer rights.</strong> Nothing in these Terms limits rights that cannot be waived under applicable
        consumer protection law.
      </p>
      <p>
        <strong>Headings.</strong> Headings are for convenience only and do not limit these Terms.
      </p>
      <p>
        <strong>Entire agreement.</strong> These Terms and the AACore Privacy Policy are the entire agreement between you
        and AAI regarding your customer use of the Platform and supersede prior agreements or communications, except for any
        separate written agreement that expressly modifies these Terms.
      </p>

      <h3 id="contact">30) Contact</h3>
      <p>
        Questions or notices regarding these Terms may be sent to{' '}
        <a href="mailto:support@aainomatic.com">support@aainomatic.com</a>.
      </p>
      <p>Mail: A&amp;A Inomatic, LLC, Clifton, VA 20124.</p>

      <div id="agreement-acknowledgment" className="rounded-lg border border-gray-200 bg-blue-50 px-4 py-3 text-sm text-gray-700 mt-8">
        <h3 className="mt-0">Agreement Acknowledgment</h3>
        <p>
          I agree to the AACore Customer User Agreement and Terms of Service and the{' '}
          <a href="/privacy">AACore Privacy Policy</a>. I consent to receive booking, account, payment, security, and
          support messages by email/SMS as described in these Terms.
        </p>
        <p>
          I acknowledge that AACore is a technology platform provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo;
          that AACore Booking is in an enhancement and trial period through December 31, 2026 unless changed by AAI, and
          that I use the Platform at my own risk.
        </p>
        <p className="mb-0">
          I further acknowledge that Businesses using AACore Booking are independent providers responsible for their own
          services, staff, licenses, prices, taxes, refunds, cancellations, policies, safety, customer care, and legal
          compliance. I acknowledge that AAI is not Stripe, is not affiliated with or endorsed by Stripe, is not a bank or
          payment processor, does not hold full payment card or banking information, does not control Customer funds or
          Business payouts, and is not responsible for Business Services or disputes between me and a Business.
        </p>
      </div>
    </AgreementPageLayout>
  );
};

export default CustomerAccountAgreementPage;
